import { Component, input, computed, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { LegalDocument, School } from '@awo-care/shared-core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-legal-read-content',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatCheckboxModule,
  ],
  template: `
    <div class="legal-container">
      <!-- Als eigene Seite ist der Dokumenttitel die Seitenueberschrift, eingebettet
           (Vertrag, Registrierungs-Stepper) bleibt er eine Zwischenueberschrift (#1102). -->
      @if (isPageHeading()) {
      <h1 class="legal-title">
        {{ documentTypeTitle() }}
        <span class="version-info">
          v.{{ legalDocument()?.version }} ({{
            legalDocument()?.validFrom
              ? ($safeNavigationMigration(legalDocument()?.validFrom?.toDate()) | date : 'dd.MM.yyyy')
              : ''
          }})</span
        >
      </h1>
      } @else {
      <h3 class="legal-title">
        {{ documentTypeTitle() }}
        <span class="version-info">
          v.{{ legalDocument()?.version }} ({{
            legalDocument()?.validFrom
              ? ($safeNavigationMigration(legalDocument()?.validFrom?.toDate()) | date : 'dd.MM.yyyy')
              : ''
          }})</span
        >
      </h3>
      }
      @if(currentSchool() && (isAGB() || showSchoolInfo())) {
      <p>
        <strong>
          <span i18n="@@lib.legal-read-content.care-offer-at">Betreuungsangebot an der</span> <br />
          {{ currentSchool()?.schoolName || placeholderSchoolName() }}</strong
        >
        <br />
        {{ currentSchool()?.address?.street || placeholderStreet() }}<br />
        {{ currentSchool()?.address?.zipCode || placeholderZipCode() }}
        {{ currentSchool()?.address?.city || placeholderCity() }}<br />
      </p>
      }

      <div class="terms-scroll-container">
        <div class="terms-content" [innerHTML]="$safeNavigationMigration(legalDocument()?.content)"></div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }

      .legal-container {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      /* h1 und h3 muessen identisch aussehen — welche Stufe gerendert wird,
         haengt am Kontext, nicht am gewuenschten Schriftbild (#1102). */
      .legal-title {
        color: #333;
        margin: 0;
        flex-shrink: 0;
        font-size: 1.17em;
        font-weight: bold;
      }

      p {
        margin: 0;
        flex-shrink: 0;
      }

      .version-info {
        font-size: 0.8rem;
        color: #666;
        font-weight: small;
      }

      .terms-scroll-container {
        flex: 1;
        min-height: 0;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        overflow-y: auto;
        background: #f9f9f9;
        margin-top: 1rem;
        margin-bottom: 1rem;

        &::-webkit-scrollbar {
          width: 8px;
        }

        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: #666;
        }
      }

      .terms-content {
        padding: 1rem;
        color: #333;
        line-height: 1.6;

        h4 {
          margin: 1.5rem 0 0.5rem;
          color: #333;
          font-size: 1.1rem;
        }

        p {
          margin: 0 0 1rem;
        }

        &:first-child {
          margin-top: 0;
        }
      }
    `,
  ],
})
export class LegalReadContentComponent {
  legalDocument = input<LegalDocument | undefined>(undefined);
  currentSchool = input<Partial<School> | undefined>(undefined);
  showSchoolInfo = input<boolean>(false);
  /** Dokument fuellt die ganze Seite -> Titel als h1 statt h3 (#1102). */
  isPageHeading = input<boolean>(false);

  isAGB = computed(() => {
    const doc = this.legalDocument();
    return !doc?.documentType || doc.documentType === 'agb';
  });

  documentTypeTitle = computed(() => {
    const doc = this.legalDocument();
    if (!doc)
      return $localize`:@@lib.legal-read-content.legal-documents:Rechtliche Dokumente`;

    return this.isAGB()
      ? $localize`:@@lib.legal-read-content.terms-and-conditions:Allgemeine Geschäftsbedingungen (AGB)`
      : $localize`:@@lib.legal-read-content.privacy-policy:Datenschutzerklärung`;
  });

  // Placeholder computed values for missing data
  placeholderSchoolName = computed(() =>
    $localize`:@@lib.legal-read-content.placeholder-school-name:{{Name der Schule}}`
  );

  placeholderStreet = computed(() =>
    $localize`:@@lib.legal-read-content.placeholder-street:{{Straße}}`
  );

  placeholderZipCode = computed(() =>
    $localize`:@@lib.legal-read-content.placeholder-zip-code:{{PLZ}}`
  );

  placeholderCity = computed(() =>
    $localize`:@@lib.legal-read-content.placeholder-city:{{Stadt}}`
  );
}
